import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { visuallyHidden } from '@mui/utils'
import { useIntl } from 'react-intl'
import ISystemStatusMessage, {
  Severity
} from '../../model/ISystemStatusMessage'
import {
  severityIcon,
  severityToken,
  SEVERITY_LABEL_ID
} from '../../utils/severityTokens'
import { formatBannerSchedule } from '../../utils/systemStatusFormat'
import { sanitizeBody } from '../../utils/sanitizeHtml'
import { messages } from './messages'
import StatusPillButton from './StatusPillButton'

interface IProps {
  message: ISystemStatusMessage
  expanded: boolean
  onToggle: (id: number) => void
  onDismiss: (id: number) => void
}

const SystemStatusRow = ({
  message,
  expanded,
  onToggle,
  onDismiss
}: IProps) => {
  const intl = useIntl()
  const token = severityToken(message.type)
  const Icon = severityIcon(message.type)
  const bodyId = `system-status-body-${message.id}`
  const severityLabel = intl.formatMessage({
    id: SEVERITY_LABEL_ID[message.type]
  })

  return (
    <Box
      role={message.type === Severity.Critical ? 'alert' : 'status'}
      sx={{
        backgroundColor: token.bg,
        borderLeft: `4px solid ${token.rail}`,
        borderBottom: `1px solid ${token.border}`
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 44,
          pr: 1,
          gap: '1rem',
          flexWrap: { xs: 'wrap', md: 'nowrap' }
        }}
      >
        <Icon sx={{ fontSize: 19, color: token.icon, ml: '8px' }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '1rem',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            minWidth: 0,
            flex: '1 1 auto'
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 500, fontSize: '1rem', color: token.title }}
          >
            <Box component="span" sx={visuallyHidden}>
              {severityLabel}:{' '}
            </Box>
            {message.title}
          </Typography>
          <Typography
            component="span"
            sx={{ fontWeight: 400, fontSize: 12.5, color: token.meta }}
          >
            {formatBannerSchedule(message)}
          </Typography>
        </Box>

        <StatusPillButton
          bg={token.bg}
          borderColor={token.border}
          textColor={token.icon}
          onClick={() => onToggle(message.id)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          sx={{ flexShrink: 0, letterSpacing: '.03em' }}
        >
          {intl.formatMessage(
            expanded ? messages.hideDetails : messages.showDetails
          )}
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          )}
        </StatusPillButton>

        <IconButton
          size="small"
          onClick={() => onDismiss(message.id)}
          aria-label={intl.formatMessage(messages.dismiss, {
            title: message.title
          })}
          sx={{
            color: token.meta,
            flexShrink: 0,
            '&:hover': { backgroundColor: 'rgba(0,0,0,.06)' }
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout={200}>
        <Box
          id={bodyId}
          sx={{
            padding: '0 40px 14px 43px',
            fontSize: 14,
            lineHeight: 1.6,
            color: token.body,
            whiteSpace: 'pre-wrap',
            '& a': { color: '#0288D1', textDecoration: 'underline' },
            '& p': { margin: '0 0 8px' },
            // Reset white-space inside lists so newlines between <li> tags in
            // the source HTML don't render as blank lines (the container uses
            // pre-wrap for plain-text bodies).
            '& ul, & ol': {
              margin: '4px 0',
              paddingLeft: '20px',
              whiteSpace: 'normal'
            },
            '& li': { margin: 0 }
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeBody(message.body) }}
        />
      </Collapse>
    </Box>
  )
}

export default SystemStatusRow
